/* SellerInsurance — unified per-buyer trade-credit dashboard.

   Single sortable table with every connected buyer, classified into:
   🟢 Allianz · 🔵 Two.inc · 🟡 Investigating · 🔴 Refused · ⚪ Not covered.
   Auto-polls every 30s while any cover is investigating, surfaces
   concentration warnings, headroom, claim eligibility timing, and
   pass-through monthly premium. Deep-link `?buyer=X&action=request` opens
   the request dialog pre-filled (so /seller/buyers can hand off to here).
*/
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api, { money } from "../../lib/api";
import { SampleInsurancePreview } from "../../components/SamplePreview";
import { PageHeader } from "../../components/PortalShell";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "../../components/ui/sheet";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import SearchAndFilter from "../../components/SearchAndFilter";
import { toast } from "sonner";
import {
  ShieldCheck, ShieldAlert, ShieldOff, Clock, AlertTriangle, CheckCircle2,
  XCircle, RefreshCw, Plus, Wallet, FileText, User, DollarSign, X,
  Sparkles, ArrowRight, Activity, Copy, ExternalLink, Receipt, TrendingUp,
} from "lucide-react";
import MasterPolicyStatusCard from "../../components/MasterPolicyStatusCard";
import InsuranceQuoteCard from "../../components/InsuranceQuoteCard";

// ─── Source badge styling ───────────────────────────────────────────────────
const SOURCE_META = {
  allianz:       { label: "Allianz cover",  icon: ShieldCheck, chip: "bg-emerald-100 text-emerald-800 border-emerald-200", dot: "bg-emerald-500" },
  two_inc:       { label: "Two.inc",        icon: Sparkles,    chip: "bg-blue-100 text-blue-800 border-blue-200",          dot: "bg-blue-500" },
  investigating: { label: "Investigating",  icon: Activity,    chip: "bg-amber-100 text-amber-800 border-amber-200",       dot: "bg-amber-500" },
  refused:       { label: "Refused",        icon: XCircle,     chip: "bg-rose-100 text-rose-800 border-rose-200",          dot: "bg-rose-500" },
  none:          { label: "Not covered",    icon: ShieldOff,   chip: "bg-secondary text-foreground/70 border-border",      dot: "bg-foreground/30" },
};

const CLAIM_STATUS = {
  filed:        { label: "Filed",        icon: FileText,     chip: "bg-blue-100 text-blue-800 border-blue-200" },
  under_review: { label: "Under review", icon: Sparkles,     chip: "bg-amber-100 text-amber-800 border-amber-200" },
  approved:     { label: "Approved",     icon: CheckCircle2, chip: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  paid:         { label: "Paid out",     icon: Wallet,       chip: "bg-emerald-200 text-emerald-900 border-emerald-300" },
  rejected:     { label: "Rejected",     icon: XCircle,      chip: "bg-rose-100 text-rose-800 border-rose-200" },
};

export default function SellerInsurance() {
  const loc = useLocation();
  const nav = useNavigate();

  const [integration, setIntegration] = useState(null);
  const [allianzSettings, setAllianzSettings] = useState(null);
  const [savingPolicy, setSavingPolicy] = useState(false);
  const [editPolicyOpen, setEditPolicyOpen] = useState(false);
  const [policyForm, setPolicyForm] = useState({ policy_id: "", business_unit_code: "US", webhook_enabled: true });
  const [exposure, setExposure] = useState(null);
  const [billing, setBilling] = useState(null);
  const [concentration, setConcentration] = useState(null);
  const [aggregate, setAggregate] = useState([]);  // unified buyer rows
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanRunning, setScanRunning] = useState(false);
  const [billingRunning, setBillingRunning] = useState(false);

  // Cover request dialog
  const [requestOpen, setRequestOpen] = useState(false);
  const [reqBuyerId, setReqBuyerId] = useState("");
  const [reqLimit, setReqLimit] = useState(100000);
  const [reqSubmitting, setReqSubmitting] = useState(false);

  // Drawer state
  const [activeBuyerId, setActiveBuyerId] = useState(null);

  // SearchAndFilter facet state
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({});

  // Auto-polling for investigating covers
  const pollRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const [intg, settings, exp, bill, conc, agg, cl] = await Promise.all([
        api.get("/insurance/v2/integration-status"),
        api.get("/insurance/v2/allianz/settings"),
        api.get("/insurance/v2/exposure"),
        api.get("/insurance/v2/billing-preview"),
        api.get("/insurance/v2/concentration"),
        api.get("/insurance/v2/buyer-aggregate"),
        api.get("/insurance/v2/claims"),
      ]);
      setIntegration(intg.data);
      setAllianzSettings(settings.data);
      setExposure(exp.data);
      setBilling(bill.data);
      setConcentration(conc.data);
      setAggregate(agg.data || []);
      setClaims(cl.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load insurance data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Auto-poll (every 30s) when any row is investigating — so the seller sees
  // the Allianz decision update without manually clicking refresh.
  useEffect(() => {
    const hasPending = aggregate.some((r) => r.source === "investigating");
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (hasPending) {
      pollRef.current = setInterval(() => { load(); }, 30000);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aggregate]);

  // Deep-link ?buyer=X&action=request — opens the dialog pre-filled.
  useEffect(() => {
    if (!aggregate.length) return;
    const sp = new URLSearchParams(loc.search);
    const buyerId = sp.get("buyer");
    const action = sp.get("action");
    if (buyerId && action === "request") {
      openRequest(buyerId);
      // clean the query string after consuming it
      nav("/seller/insurance", { replace: true });
    } else if (buyerId) {
      setActiveBuyerId(buyerId);
      nav("/seller/insurance", { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aggregate.length]);

  const openRequest = (buyerId) => {
    setReqBuyerId(buyerId || "");
    setReqLimit(100000);
    setRequestOpen(true);
  };

  const submitRequest = async () => {
    if (!reqBuyerId) return toast.error("Pick a buyer");
    if (!reqLimit || reqLimit <= 0) return toast.error("Limit must be positive");
    setReqSubmitting(true);
    try {
      const { data } = await api.post("/insurance/v2/buyer-covers/request", {
        buyer_id: reqBuyerId,
        requested_limit: Number(reqLimit),
      });
      toast.success(
        data.status === "accepted"
          ? `Allianz approved · ${money(data.permanent_limit)}`
          : data.status === "investigating"
            ? "Allianz is investigating · we'll auto-poll for the decision"
            : data.status === "refused"
              ? `Allianz refused${data.refusal_reason ? ` · ${data.refusal_reason}` : ""}`
              : "Cover request sent"
      );
      setRequestOpen(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cover request failed");
    } finally {
      setReqSubmitting(false);
    }
  };

  const refreshCover = async (cid) => {
    try { await api.post(`/insurance/v2/buyer-covers/${cid}/refresh`); await load(); }
    catch { /* silent */ }
  };

  const cancelCover = async (cid) => {
    if (!window.confirm("Cancel this cover? Future invoices to this buyer will not be insured.")) return;
    try {
      await api.delete(`/insurance/v2/buyer-covers/${cid}`);
      toast.success("Cover cancelled");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cancel failed");
    }
  };

  const triggerScan = async () => {
    setScanRunning(true);
    try {
      const { data } = await api.post("/insurance/v2/scan");
      toast.success(`Scan complete · ${data.claims_filed} claim(s) filed`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Scan failed");
    } finally { setScanRunning(false); }
  };

  const triggerBilling = async () => {
    setBillingRunning(true);
    try {
      const { data } = await api.post("/insurance/v2/billing/run");
      toast.success(
        data.debited
          ? `Premium debited · ${data.debited} seller wallet(s)`
          : "Already debited for this month — idempotent"
      );
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Billing run failed");
    } finally { setBillingRunning(false); }
  };

  const openEditPolicy = () => {
    setPolicyForm({
      policy_id: allianzSettings?.policy_id || "",
      business_unit_code: allianzSettings?.business_unit_code || "US",
      webhook_enabled: allianzSettings?.webhook_enabled !== false,
    });
    setEditPolicyOpen(true);
  };

  const savePolicy = async () => {
    if (!policyForm.policy_id?.trim()) {
      return toast.error("Policy ID is required");
    }
    setSavingPolicy(true);
    try {
      const { data } = await api.put("/insurance/v2/allianz/settings", policyForm);
      setAllianzSettings({ ...(allianzSettings || {}), ...data });
      toast.success("Allianz policy saved");
      setEditPolicyOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save policy");
    } finally { setSavingPolicy(false); }
  };

  // ── Filtered rows ────────────────────────────────────────────────────
  const filteredRows = useMemo(() => {
    let r = aggregate;
    const F = filters || {};
    if (F.source?.values?.length) r = r.filter((x) => F.source.values.includes(x.source));
    if (F.score) {
      r = r.filter((x) => {
        const v = x.score || 0;
        if (F.score.op === "gt") return v > F.score.value;
        if (F.score.op === "lt") return v < F.score.value;
        if (F.score.op === "between") return v >= F.score.min && v <= F.score.max;
        return true;
      });
    }
    if (search) r = r.filter((x) => JSON.stringify(x).toLowerCase().includes(search.toLowerCase()));
    return r;
  }, [aggregate, filters, search]);

  const activeRow = aggregate.find((r) => r.buyer_id === activeBuyerId);

  if (loading && !exposure) {
    return <div className="p-10 text-sm text-foreground/55">Loading insurance…</div>;
  }

  return (
    <div data-testid="seller-insurance">
      <PageHeader
        title="Trade Credit Insurance"
        subtitle="Per-buyer cover · Allianz Trade · pass-through pricing"
        action={
          <div className="flex gap-2">
            <Button onClick={triggerScan} disabled={scanRunning} variant="outline" data-testid="ins-scan" className="rounded-full">
              <RefreshCw className={`w-4 h-4 mr-1.5 ${scanRunning ? "animate-spin" : ""}`}/>{scanRunning ? "Scanning…" : "Run scan"}
            </Button>
            <Button onClick={() => openRequest("")} data-testid="ins-request-cover" className="rounded-full bg-foreground text-background hover:bg-foreground/90">
              <Plus className="w-4 h-4 mr-1.5"/>Request cover
            </Button>
          </div>
        }
      />

      <div className="p-8 space-y-6">

        {/* Stage-1 Master policy application status — top priority */}
        <MasterPolicyStatusCard />
        <InsuranceQuoteCard />

        {/* Provider banner */}
        {integration && (
          <div
            className={`rounded-2xl border px-5 py-4 flex items-center gap-4 ${integration.live ? "bg-emerald-50/40 border-emerald-200" : "bg-amber-50/40 border-amber-200"}`}
            data-testid="ins-integration-banner"
          >
            <div className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 ${integration.live ? "bg-emerald-500 text-white" : "bg-amber-500 text-white"}`}>
              {integration.live ? <ShieldCheck className="w-5 h-5"/> : <ShieldAlert className="w-5 h-5"/>}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium">
                {integration.live ? `Allianz Trade · live (${integration.broker_code})` : "Allianz Trade · sandbox simulator"}
              </div>
              <div className="text-[11.5px] text-foreground/60 mt-0.5">
                {integration.live
                  ? "Live cover requests + claims. Premiums pass-through, billed monthly to your wallet."
                  : "Mock provider returning realistic responses. Wire ALLIANZ_CLIENT_ID/SECRET to go live."}
              </div>
            </div>
            <div className="text-[10px] mono uppercase tracking-wider text-foreground/50 shrink-0">{integration.mode}</div>
          </div>
        )}

        {/* Allianz Policy connect — visible whenever live mode is on */}
        {allianzSettings && integration?.live && (
          <div
            className={`rounded-2xl border px-5 py-4 ${allianzSettings.configured ? "border-border bg-white" : "border-amber-300 bg-amber-50/40"}`}
            data-testid="allianz-policy-card"
          >
            <div className="flex items-start gap-4">
              <div className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 ${allianzSettings.configured ? "bg-foreground text-background" : "bg-amber-500 text-white"}`}>
                <FileText className="w-5 h-5"/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/50">
                  {allianzSettings.configured ? "Your Allianz Trade policy" : "Connect your Allianz policy"}
                </div>
                <div className="text-sm font-medium mt-0.5">
                  {allianzSettings.configured ? (
                    <>Policy <span className="mono">{allianzSettings.policy_id}</span> · Business unit {allianzSettings.business_unit_code}</>
                  ) : (
                    "Add your policy ID to issue real cover decisions"
                  )}
                </div>
                <div className="text-[11.5px] text-foreground/60 mt-1 leading-snug">
                  {allianzSettings.configured
                    ? "Cover requests will route to this policy. Webhook updates land in real-time when Allianz issues a decision."
                    : "Allianz issues a unique alphanumeric policyId (≤10 chars) per broker. Paste it once — every future cover request uses it automatically."}
                </div>
              </div>
              <Button
                onClick={openEditPolicy}
                variant={allianzSettings.configured ? "outline" : "default"}
                className="rounded-full"
                data-testid="allianz-policy-edit"
              >
                {allianzSettings.configured ? "Edit" : "Connect policy"}
              </Button>
            </div>
          </div>
        )}

        {/* Concentration warning */}
        {concentration?.warning && (
          <div className="rounded-2xl border border-amber-300 bg-amber-50/60 px-5 py-3 flex items-center gap-3" data-testid="ins-concentration">
            <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0"/>
            <div className="text-[12.5px] text-amber-900 leading-snug">
              <strong>{Math.round((concentration.ratio || 0) * 100)}%</strong> of your covered exposure sits on{" "}
              <strong>{concentration.top?.buyer_name}</strong>. Allianz portfolios are penalised above 40% — consider spreading cover across more buyers.
            </div>
          </div>
        )}

        {/* KPI tiles */}
        {exposure && billing && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="ins-kpis">
            <KPI
              label="Covered buyers"
              value={exposure.active_cover_count}
              hint={exposure.pending_cover_count > 0 ? `${exposure.pending_cover_count} investigating` : "Active Allianz cover"}
            />
            <KPI label="Total exposure" value={money(exposure.total_covered_limit)} hint="Sum of approved limits"/>
            <KPI label="Open claims" value={exposure.open_claims} hint={`${money(exposure.open_payout_value)} expected`} tone={exposure.open_claims > 0 ? "warn" : "default"}/>
            <KPI
              label="Next month premium"
              value={money(billing.next_month_premium)}
              hint={`${billing.active_covers} cover(s) · auto-debit · ${billing.billing_provider}`}
              action={<button onClick={triggerBilling} disabled={billingRunning} data-testid="ins-billing-run" className="text-[10px] mono uppercase tracking-wider text-foreground/55 hover:text-foreground inline-flex items-center gap-1">
                <Wallet className="w-2.5 h-2.5"/>{billingRunning ? "Running…" : "Run debit"}
              </button>}
            />
          </div>
        )}

        {/* Filters */}
        <SearchAndFilter
          page="seller_orders"
          search={search}
          onSearchChange={setSearch}
          filters={filters}
          onChange={setFilters}
          totalCount={filteredRows.length}
          facets={[
            {
              id: "source", label: "Coverage source", icon: ShieldCheck, type: "multi",
              options: [
                { value: "allianz",       label: "Covered (Allianz)" },
                { value: "two_inc",       label: "Covered (Two.inc)" },
                { value: "investigating", label: "Investigating" },
                { value: "refused",       label: "Refused" },
                { value: "none",          label: "Not covered" },
              ],
            },
            { id: "score", label: "Risk score", icon: TrendingUp, type: "numeric" },
          ]}
        />

        {/* Unified buyer table */}
        <BuyerTable
          rows={filteredRows}
          onOpen={(r) => setActiveBuyerId(r.buyer_id)}
          onRequestCover={openRequest}
          onRefreshCover={refreshCover}
          onCancelCover={cancelCover}
        />

        {/* Open claims (collapsible-style) */}
        {claims.filter((c) => ["filed", "under_review", "approved"].includes(c.status)).length > 0 && (
          <ClaimsPanel claims={claims}/>
        )}

      </div>

      {/* ── Drawer: buyer detail ── */}
      <Sheet open={!!activeRow} onOpenChange={(v) => !v && setActiveBuyerId(null)}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="ins-cover-drawer">
          <SheetTitle className="sr-only">Insurance · buyer detail</SheetTitle>
          {activeRow && (
            <BuyerDrawer
              row={activeRow}
              claims={claims.filter((c) => c.buyer_id === activeRow.buyer_id)}
              onClose={() => setActiveBuyerId(null)}
              onRequest={() => openRequest(activeRow.buyer_id)}
              onRefresh={() => activeRow.cover && refreshCover(activeRow.cover.id)}
              onCancel={() => activeRow.cover && cancelCover(activeRow.cover.id)}
              onIncrease={() => openRequest(activeRow.buyer_id)}
            />
          )}
        </SheetContent>
      </Sheet>

      {/* ── Cover request dialog ── */}
      <Dialog open={requestOpen} onOpenChange={setRequestOpen}>
        <DialogContent className="max-w-md" data-testid="ins-request-dialog">
          <DialogHeader>
            <DialogTitle className="display text-2xl tracking-tight">Request buyer cover</DialogTitle>
            <DialogDescription className="text-xs text-foreground/60">
              Allianz Trade reviews the buyer and either auto-approves a permanent limit or asks for a few hours to investigate.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-xs mono uppercase tracking-[0.18em] text-foreground/50">Buyer</Label>
              <select
                value={reqBuyerId}
                onChange={(e) => setReqBuyerId(e.target.value)}
                data-testid="ins-req-buyer"
                className="w-full h-11 mt-1.5 rounded-md border border-input bg-white px-3 text-sm"
              >
                <option value="">Select a buyer…</option>
                {aggregate.map((r) => (
                  <option key={r.buyer_id} value={r.buyer_id}>
                    {`${r.buyer_name} · score ${r.score}`}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs mono uppercase tracking-[0.18em] text-foreground/50">Requested credit limit</Label>
              <div className="relative mt-1.5">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40 text-sm">$</span>
                <Input
                  type="number"
                  value={reqLimit}
                  onChange={(e) => setReqLimit(e.target.value)}
                  placeholder="100,000"
                  data-testid="ins-req-limit"
                  className="h-11 pl-7 mono"
                />
              </div>
              <div className="flex gap-1.5 mt-2 flex-wrap">
                {[25000, 100000, 250000, 500000].map((v) => (
                  <button
                    key={v}
                    onClick={() => setReqLimit(v)}
                    data-testid={`ins-limit-${v}`}
                    className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${Number(reqLimit) === v ? "border-foreground bg-foreground/[0.03]" : "border-border hover:border-foreground/30"}`}
                  >
                    {money(v)}
                  </button>
                ))}
              </div>
            </div>
            <div className="rounded-xl bg-secondary/40 border border-border p-3 text-[11.5px] text-foreground/65 leading-relaxed">
              Premium auto-debits monthly from your verqo wallet — pass-through, no platform markup. Buyers paying via Two.inc are excluded (they're already insured).
            </div>
          </div>
          <Button
            onClick={submitRequest}
            disabled={reqSubmitting}
            data-testid="ins-req-submit"
            className="mt-4 w-full h-11 rounded-full bg-foreground text-background hover:bg-foreground/90"
          >
            {reqSubmitting ? "Requesting…" : "Send to Allianz"}<ArrowRight className="w-4 h-4 ml-1.5"/>
          </Button>
        </DialogContent>
      </Dialog>

      {/* Allianz policy connect dialog */}
      <Dialog open={editPolicyOpen} onOpenChange={setEditPolicyOpen}>
        <DialogContent className="max-w-md" data-testid="allianz-policy-dialog">
          <DialogHeader>
            <DialogTitle className="display text-2xl tracking-tight">
              {allianzSettings?.configured ? "Edit Allianz policy" : "Connect Allianz Trade"}
            </DialogTitle>
            <DialogDescription className="text-[12.5px] text-foreground/60">
              Allianz issued you a unique policy ID when your account was activated. Paste it below — every cover request from this seller account will route to that policy.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs mono uppercase tracking-[0.18em] text-foreground/50">Policy ID</Label>
              <Input
                value={policyForm.policy_id}
                onChange={(e) => setPolicyForm({ ...policyForm, policy_id: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10) })}
                placeholder="e.g. POL12345"
                className="mt-1.5 h-11 mono"
                data-testid="allianz-policy-input"
                maxLength={10}
              />
              <div className="text-[10.5px] text-foreground/55 mt-1">Alphanumeric · ≤10 chars · case-insensitive</div>
            </div>
            <div>
              <Label className="text-xs mono uppercase tracking-[0.18em] text-foreground/50">Business unit code</Label>
              <Input
                value={policyForm.business_unit_code}
                onChange={(e) => setPolicyForm({ ...policyForm, business_unit_code: e.target.value.toUpperCase().slice(0, 8) })}
                placeholder="US"
                className="mt-1.5 h-11 mono"
                data-testid="allianz-business-unit-input"
              />
              <div className="text-[10.5px] text-foreground/55 mt-1">Default <span className="mono">US</span> — change only if Allianz issued you a different region.</div>
            </div>
            <label className="flex items-center gap-2.5 text-[13px] cursor-pointer p-2 rounded-md hover:bg-secondary/40">
              <input
                type="checkbox"
                checked={policyForm.webhook_enabled}
                onChange={(e) => setPolicyForm({ ...policyForm, webhook_enabled: e.target.checked })}
                className="w-4 h-4"
                data-testid="allianz-webhook-toggle"
              />
              <span>
                Enable real-time webhook updates
                <span className="block text-[11px] text-foreground/55 mt-0.5">Allianz POSTs cover decisions to <span className="mono">/api/webhooks/allianz</span> — eliminates polling.</span>
              </span>
            </label>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setEditPolicyOpen(false)} className="flex-1 h-11 rounded-full">
                Cancel
              </Button>
              <Button
                onClick={savePolicy}
                disabled={savingPolicy}
                data-testid="allianz-policy-save"
                className="flex-1 h-11 rounded-full bg-foreground text-background hover:bg-foreground/90"
              >
                {savingPolicy ? "Saving…" : (allianzSettings?.configured ? "Save changes" : "Connect policy")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────
function KPI({ label, value, hint, tone, action }) {
  const toneClass = tone === "warn" ? "text-amber-700" : tone === "success" ? "text-emerald-700" : "";
  return (
    <div className="rounded-2xl border border-border bg-white p-5">
      <div className="flex items-start justify-between gap-2">
        <div className="text-[10px] mono uppercase tracking-[0.15em] text-foreground/50">{label}</div>
        {action}
      </div>
      <div className={`display text-2xl mt-2 tracking-tight ${toneClass}`}>{value}</div>
      {hint && <div className="text-[11px] text-foreground/55 mt-1 truncate">{hint}</div>}
    </div>
  );
}

function SourceChip({ source, twoIncBadge }) {
  const m = SOURCE_META[source] || SOURCE_META.none;
  const Icon = m.icon;
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] border ${m.chip}`}>
        <Icon className="w-3 h-3"/>{m.label}
      </span>
      {twoIncBadge && source !== "two_inc" && (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] bg-blue-50 text-blue-700 border border-blue-200" title="Some invoices financed by Two.inc">
          <Sparkles className="w-2.5 h-2.5"/>+ Two.inc
        </span>
      )}
    </span>
  );
}

function ClaimChip({ status }) {
  const m = CLAIM_STATUS[status] || CLAIM_STATUS.filed;
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] border ${m.chip}`}>
      <Icon className="w-3 h-3"/>{m.label}
    </span>
  );
}

function BuyerTable({ rows, onOpen, onRequestCover, onRefreshCover, onCancelCover }) {
  // When the seller has zero connected buyers (and thus zero insurance
  // covers), render the compliance-aware Sample Insurance preview instead
  // of the bare empty-state message. The preview shows ZERO fake dollar
  // amounts to avoid implying Allianz coverage that doesn't exist.
  if (rows.length === 0) {
    return <SampleInsurancePreview />;
  }
  return (
    <div className="rounded-2xl border border-border bg-white overflow-hidden" data-testid="ins-buyer-table">
      <div className="grid grid-cols-[2fr_180px_140px_140px_140px_120px] gap-4 px-5 py-3 bg-secondary/50 border-b border-border text-[10px] mono uppercase tracking-[0.2em] text-foreground/55">
        <div>Buyer</div>
        <div>Coverage</div>
        <div>Limit / used</div>
        <div>Outstanding</div>
        <div>Premium / yr</div>
        <div className="text-right">Actions</div>
      </div>
      <div className="divide-y divide-border">
        {rows.map((r) => {
          const annualPremium = (r.limit || 0) * (r.premium_rate || 0);
          const usedPct = r.limit ? Math.min(100, (r.used / r.limit) * 100) : 0;
          const isPending = r.source === "investigating";
          return (
            <div
              key={r.buyer_id}
              onClick={() => onOpen(r)}
              data-testid={`ins-row-${r.buyer_id}`}
              className="grid grid-cols-[2fr_180px_140px_140px_140px_120px] gap-4 px-5 py-4 items-center hover:bg-secondary/30 cursor-pointer"
            >
              {/* Buyer */}
              <div className="min-w-0 flex items-center gap-3">
                <div className={`w-2 h-2 rounded-full ${SOURCE_META[r.source]?.dot || "bg-foreground/30"} ${isPending ? "animate-pulse" : ""}`}/>
                <div className="min-w-0">
                  <div className="font-medium text-sm truncate">{r.buyer_name}</div>
                  <div className="text-[11px] text-foreground/55 truncate">
                    {r.buyer_email}
                    {r.score && <> · score {r.score}</>}
                  </div>
                </div>
              </div>
              {/* Coverage */}
              <div>
                <SourceChip source={r.source} twoIncBadge={r.has_two_inc_invoices}/>
                {isPending && r.cover?.investigation_eta && (
                  <div className="text-[10px] text-foreground/55 mt-1">
                    ETA {new Date(r.cover.investigation_eta).toLocaleDateString()}
                  </div>
                )}
              </div>
              {/* Limit / used */}
              <div>
                {r.source === "allianz" ? (
                  <>
                    <div className="display text-sm">{money(r.limit)}</div>
                    <div className="h-1 mt-1 bg-secondary rounded-full overflow-hidden">
                      <div className={`h-full ${usedPct > 80 ? "bg-rose-500" : usedPct > 50 ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${usedPct}%` }}/>
                    </div>
                    <div className="text-[10px] text-foreground/55 mt-0.5">{money(r.used)} used · {money(r.available)} free</div>
                  </>
                ) : (
                  <span className="text-foreground/35 text-sm">—</span>
                )}
              </div>
              {/* Outstanding */}
              <div>
                <div className="text-sm">{money(r.outstanding)}</div>
                {r.outstanding > 0 && r.source === "allianz" && r.claim_eligible_days != null && (
                  <div className={`text-[10px] mt-0.5 ${r.claim_eligible_days === 0 ? "text-rose-700" : "text-foreground/55"}`}>
                    {r.claim_eligible_days === 0
                      ? "Claim-eligible now"
                      : `Claim in ${r.claim_eligible_days}d`}
                  </div>
                )}
              </div>
              {/* Premium */}
              <div>
                {r.source === "allianz" ? (
                  <>
                    <div className="text-sm">{(r.premium_rate * 100).toFixed(2)}%</div>
                    <div className="text-[10px] text-foreground/55">{money(annualPremium)}/yr</div>
                  </>
                ) : (
                  <span className="text-foreground/35 text-sm">—</span>
                )}
              </div>
              {/* Actions */}
              <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                {r.source === "none" && (
                  <Button size="sm" onClick={() => onRequestCover(r.buyer_id)} data-testid={`ins-add-${r.buyer_id}`} className="rounded-full bg-foreground text-background hover:bg-foreground/90 text-[11px] h-7 px-3">
                    <Plus className="w-3 h-3 mr-1"/>Request
                  </Button>
                )}
                {r.source === "refused" && (
                  <Button size="sm" variant="outline" onClick={() => onRequestCover(r.buyer_id)} data-testid={`ins-rerequest-${r.buyer_id}`} className="rounded-full text-[11px] h-7 px-3">
                    Re-request
                  </Button>
                )}
                {r.source === "investigating" && r.cover?.id && (
                  <button onClick={() => onRefreshCover(r.cover.id)} data-testid={`ins-poll-${r.buyer_id}`} className="px-2.5 py-1 rounded-full text-[11px] border border-border hover:border-foreground/40 inline-flex items-center gap-1">
                    <RefreshCw className="w-2.5 h-2.5"/>Poll
                  </button>
                )}
                {r.source === "allianz" && r.cover?.id && (
                  <button onClick={() => onCancelCover(r.cover.id)} data-testid={`ins-cancel-${r.buyer_id}`} className="w-7 h-7 rounded-full hover:bg-rose-50 hover:text-rose-700 grid place-items-center text-foreground/60" title="Cancel cover">
                    <X className="w-3 h-3"/>
                  </button>
                )}
                {r.source === "two_inc" && (
                  <span className="text-[10px] text-foreground/45 mono uppercase tracking-wider">via Two.inc</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ClaimsPanel({ claims }) {
  const open = claims.filter((c) => ["filed", "under_review", "approved"].includes(c.status));
  return (
    <div className="rounded-2xl border border-border bg-white overflow-hidden" data-testid="ins-claims-panel">
      <div className="px-5 py-3 border-b border-border bg-secondary/30 flex items-center gap-2">
        <FileText className="w-4 h-4"/>
        <div className="text-sm font-medium">Open claims</div>
        <span className="ml-auto text-[10px] mono uppercase tracking-wider text-foreground/55">{open.length}</span>
      </div>
      <div className="divide-y divide-border">
        {open.map((c) => (
          <div key={c.id} className="px-5 py-3 flex items-center gap-3" data-testid={`ins-open-claim-${c.id}`}>
            <Receipt className="w-4 h-4 text-foreground/55 shrink-0"/>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{c.invoice_code} · {c.buyer_name}</div>
              <div className="text-[11px] text-foreground/55">
                Filed {new Date(c.filed_at).toLocaleDateString()} · {c.days_past_due_at_filing}d past due · expected {money(c.expected_payout)}
              </div>
            </div>
            <ClaimChip status={c.status}/>
          </div>
        ))}
      </div>
    </div>
  );
}

function BuyerDrawer({ row, claims, onClose, onRequest, onRefresh, onCancel, onIncrease }) {
  const cov = row.cover;
  const annualPremium = (row.limit || 0) * (row.premium_rate || 0);
  const copy = (val) => {
    if (!val) return;
    navigator.clipboard.writeText(val);
    toast.success("Copied");
  };
  return (
    <div className="space-y-5">
      <SheetHeader>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] mono uppercase tracking-[0.2em] text-foreground/50">Insurance</div>
            <div className="display text-2xl tracking-tight mt-1 truncate" data-testid="ins-drawer-buyer">{row.buyer_name}</div>
            <div className="text-[12px] text-foreground/55 truncate">{row.buyer_email} · score {row.score}</div>
          </div>
          <button onClick={onClose} data-testid="ins-drawer-close" className="w-8 h-8 rounded-full hover:bg-secondary grid place-items-center text-foreground/50">
            <X className="w-4 h-4"/>
          </button>
        </div>
      </SheetHeader>

      {/* Status card */}
      <div className="rounded-xl border border-border bg-white p-4 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <SourceChip source={row.source} twoIncBadge={row.has_two_inc_invoices}/>
          {cov?.refusal_reason && (
            <div className="text-[11.5px] text-rose-700 mt-2">{cov.refusal_reason}</div>
          )}
          {cov?.investigation_eta && row.source === "investigating" && (
            <div className="text-[11px] text-amber-700 mt-2">ETA · {new Date(cov.investigation_eta).toLocaleString()}</div>
          )}
          {row.source === "two_inc" && (
            <div className="text-[11.5px] text-foreground/65 mt-2">
              {row.two_inc_invoice_count} invoice(s) financed by Two.inc — credit risk carried by Two.inc.
            </div>
          )}
        </div>
        <div className="flex flex-col gap-1.5 shrink-0 items-end">
          {row.source === "none" && (
            <Button size="sm" onClick={onRequest} data-testid="ins-drawer-request" className="rounded-full bg-foreground text-background hover:bg-foreground/90"><Plus className="w-3 h-3 mr-1"/>Request</Button>
          )}
          {row.source === "refused" && (
            <Button size="sm" variant="outline" onClick={onRequest} className="rounded-full">Re-request</Button>
          )}
          {row.source === "investigating" && (
            <Button size="sm" variant="outline" onClick={onRefresh} className="rounded-full"><RefreshCw className="w-3 h-3 mr-1"/>Poll</Button>
          )}
          {row.source === "allianz" && (
            <>
              <Button size="sm" variant="outline" onClick={onIncrease} className="rounded-full">Increase</Button>
              <Button size="sm" variant="outline" onClick={onCancel} className="rounded-full"><X className="w-3 h-3 mr-1"/>Cancel</Button>
            </>
          )}
        </div>
      </div>

      {/* Allianz facts */}
      {row.source === "allianz" && (
        <>
          <div className="grid grid-cols-3 gap-2">
            <MiniStat label="Approved limit"  value={money(row.limit)}/>
            <MiniStat label="Used"            value={money(row.used)}/>
            <MiniStat label="Available"       value={money(row.available)}/>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <MiniStat label="Premium / yr"    value={`${(row.premium_rate * 100).toFixed(2)}%`}/>
            <MiniStat label="Annual cost"     value={money(annualPremium)}/>
            <MiniStat label="Outstanding"     value={money(row.outstanding)}/>
          </div>
        </>
      )}

      {/* Allianz reference */}
      {cov?.allianz_cover_id && (
        <div>
          <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/55 mb-3">Allianz reference</div>
          <div className="rounded-lg bg-secondary/30 border border-border p-3 text-[12px] space-y-1.5 mono">
            <Row k="Cover ID" v={cov.allianz_cover_id} onCopy={() => copy(cov.allianz_cover_id)}/>
            {cov.allianz_policy_id && <Row k="Policy ID" v={cov.allianz_policy_id} onCopy={() => copy(cov.allianz_policy_id)}/>}
            {cov.allianz_business_unit && <Row k="Business unit" v={cov.allianz_business_unit}/>}
            <Row k="Mode" v={cov.is_mock ? "Sandbox simulator" : "Live"}/>
            <Row k="Renews" v={cov.expires_at ? new Date(cov.expires_at).toLocaleDateString() : "—"}/>
          </div>
        </div>
      )}

      {/* Claim eligibility */}
      {row.source === "allianz" && row.outstanding > 0 && row.claim_eligible_days != null && (
        <div className={`rounded-xl border p-3 text-[12px] ${row.claim_eligible_days === 0 ? "border-rose-200 bg-rose-50/40 text-rose-900" : "border-border bg-secondary/30 text-foreground/70"}`} data-testid="ins-drawer-claim-eligibility">
          <Clock className="w-3.5 h-3.5 inline mr-1.5"/>
          {row.claim_eligible_days === 0
            ? "An overdue invoice is eligible for claim filing — Allianz takes over collections automatically."
            : `Earliest claim filing: in ${row.claim_eligible_days} day(s) (the engine auto-files at +15 days past due).`}
        </div>
      )}

      {/* Claims for this buyer */}
      {claims.length > 0 && (
        <div>
          <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/55 mb-3">Claims</div>
          <div className="space-y-2">
            {claims.map((c) => (
              <div key={c.id} className="rounded-lg border border-border bg-white px-3 py-2.5 flex items-center justify-between gap-3 text-sm">
                <div className="min-w-0">
                  <div className="font-medium truncate">{c.invoice_code}</div>
                  <div className="text-[11px] text-foreground/55">{money(c.amount)} · expected {money(c.expected_payout)}{c.status === "paid" && <> · paid {money(c.actual_payout)}</>}</div>
                </div>
                <ClaimChip status={c.status}/>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Cover events timeline */}
      {cov?.events && cov.events.length > 0 && (
        <div>
          <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/55 mb-3">Status timeline</div>
          <div className="space-y-3 border-l-2 border-border pl-5">
            {cov.events.map((e, i) => (
              <div key={i} className="relative">
                <div className="absolute -left-[26px] w-3 h-3 rounded-full bg-foreground/60 ring-4 ring-background"/>
                <div className="text-[12.5px] font-medium capitalize">{e.status}</div>
                {e.note && <div className="text-[11px] text-foreground/55 mt-0.5">{e.note}</div>}
                <div className="text-[9.5px] mono uppercase tracking-wider text-foreground/45 mt-1">
                  {new Date(e.at).toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Wallet link */}
      <div className="rounded-xl bg-secondary/30 border border-border p-3 text-[11.5px] text-foreground/65 flex items-center gap-2">
        <Wallet className="w-3.5 h-3.5 shrink-0"/>
        Premiums auto-debit from your verqo wallet. <a href="/seller/wallet" className="ml-1 underline inline-flex items-center gap-0.5">Open wallet<ExternalLink className="w-3 h-3"/></a>
      </div>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="rounded-xl border border-border bg-white p-3">
      <div className="text-[9px] mono uppercase tracking-wider text-foreground/55">{label}</div>
      <div className="display text-sm mt-0.5 truncate">{value}</div>
    </div>
  );
}

function Row({ k, v, onCopy }) {
  return (
    <div className="flex justify-between items-center gap-2">
      <span className="text-foreground/55">{k}</span>
      <span className="truncate inline-flex items-center gap-1">
        <span>{v}</span>
        {onCopy && (
          <button onClick={onCopy} className="text-foreground/40 hover:text-foreground" title="Copy"><Copy className="w-2.5 h-2.5"/></button>
        )}
      </span>
    </div>
  );
}
